import { supabase } from '@/integrations/supabase/client';

export interface BotWinRateStats {
  bot_id: string;
  total_forwarded_exits: number;
  wins: number;
  losses: number;
  win_rate: number;
}

export interface ForwardedSignal {
  id: string;
  bot_id: string;
  signal_timestamp: string;
  action: string;
  instrument: string;
  win: number;
  response_status: number;
  response_ms: number | null;
  is_virtual_forward: boolean;
}

export interface Type2SystemStats {
  total_bots: number;
  active_bots: number;
  total_signals: number;
  forwarded_signals: number;
  skipped_signals: number;
  total_trades: number;
  wins: number;
  losses: number;
  system_win_rate: number;
  
  // All signals stats (before filter)
  all_signals_trades: number;
  all_signals_wins: number;
  all_signals_losses: number;
  all_signals_win_rate: number;
  
  // Improvement metrics
  win_rate_improvement_pts: number;
  win_rate_improvement_pct: number;
  
  forward_rate: number;
  skip_rate: number;
}

export interface Type2BotPerformance {
  bot_id: string;
  bot_name: string;
  total_signals: number;
  forwarded: number;
  skipped: number;
  trades: number;
  wins: number;
  losses: number;
  win_rate: number;
  
  // All signals stats
  all_signals_trades: number;
  all_signals_wins: number;
  all_signals_losses: number;
  all_signals_win_rate: number;
  
  // Improvement metrics
  win_rate_improvement_pts: number;
  win_rate_improvement_pct: number;
  
  forward_rate: number;
  is_active: boolean;
  last_signal_at: string | null;
  
  // Permanent lock info
  active_lock_pattern_id?: string | null;
  is_permanent_locked?: boolean;
  lock_pattern?: string;
}

export async function fetchBotWinRateStats(botId: string): Promise<BotWinRateStats> {
  try {
    // Query 1: Count total EXIT signals that were forwarded (response_status = 200)
    const { count: totalExits } = await supabase
      .from('conditional_skip_events')
      .select('*', { count: 'exact', head: true })
      .eq('bot_id', botId)
      .in('action', ['EXIT_LONG', 'EXIT_SHORT'])
      .eq('response_status', 200);
    
    // Query 2: Count wins (win = 1 and response_status = 200)
    const { count: wins } = await supabase
      .from('conditional_skip_events')
      .select('*', { count: 'exact', head: true })
      .eq('bot_id', botId)
      .in('action', ['EXIT_LONG', 'EXIT_SHORT'])
      .eq('response_status', 200)
      .eq('win', 1);
    
    // Query 3: Count losses (win = 0 and response_status = 200)
    const { count: losses } = await supabase
      .from('conditional_skip_events')
      .select('*', { count: 'exact', head: true })
      .eq('bot_id', botId)
      .in('action', ['EXIT_LONG', 'EXIT_SHORT'])
      .eq('response_status', 200)
      .eq('win', 0);
    
    const totalForwardedExits = totalExits || 0;
    const winCount = wins || 0;
    const lossCount = losses || 0;
    const winRate = totalForwardedExits > 0 ? (winCount / totalForwardedExits) * 100 : 0;
    
    return {
      bot_id: botId,
      total_forwarded_exits: totalForwardedExits,
      wins: winCount,
      losses: lossCount,
      win_rate: winRate
    };
  } catch (error) {
    console.error('Error fetching bot win rate stats:', error);
    return {
      bot_id: botId,
      total_forwarded_exits: 0,
      wins: 0,
      losses: 0,
      win_rate: 0
    };
  }
}

export async function fetchAllBotsWinRateStats(): Promise<Map<string, BotWinRateStats>> {
  try {
    // Get all unique bot_ids with EXIT signals that have response_status = 200
    const { data: botIds } = await supabase
      .from('conditional_skip_events')
      .select('bot_id')
      .in('action', ['EXIT_LONG', 'EXIT_SHORT'])
      .eq('response_status', 200);
    
    if (!botIds || botIds.length === 0) {
      return new Map();
    }
    
    const uniqueBotIds = [...new Set(botIds.map(b => b.bot_id))];
    
    const statsMap = new Map<string, BotWinRateStats>();
    
    // Fetch stats for each bot
    await Promise.all(
      uniqueBotIds.map(async (botId) => {
        const stats = await fetchBotWinRateStats(botId);
        statsMap.set(botId, stats);
      })
    );
    
    return statsMap;
  } catch (error) {
    console.error('Error fetching all bots win rate stats:', error);
    return new Map();
  }
}

export async function fetchBotExitSignals(
  botId: string,
  page: number = 1,
  pageSize: number = 20,
  winFilter: 'all' | 'win' | 'loss' = 'all',
  searchQuery: string = '',
  forwardTypeFilter: 'all' | 'real' | 'virtual' = 'all'
): Promise<{
  signals: ForwardedSignal[];
  total: number;
  page: number;
  pageSize: number;
}> {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    
    // Build query
    let query = supabase
      .from('conditional_skip_events')
      .select('id, bot_id, signal_timestamp, action, instrument, win, response_status, response_ms, is_virtual_forward', { count: 'exact' })
      .eq('bot_id', botId)
      .in('action', ['EXIT_LONG', 'EXIT_SHORT'])
      .eq('response_status', 200);
    
    // Apply win filter
    if (winFilter === 'win') {
      query = query.eq('win', 1);
    } else if (winFilter === 'loss') {
      query = query.eq('win', 0);
    }
    
    // Apply forward type filter
    if (forwardTypeFilter === 'real') {
      query = query.eq('is_virtual_forward', false);
    } else if (forwardTypeFilter === 'virtual') {
      query = query.eq('is_virtual_forward', true);
    }
    
    // Apply search filter
    if (searchQuery) {
      query = query.ilike('instrument', `%${searchQuery}%`);
    }
    
    // Apply ordering and pagination
    const { data: signals, error, count } = await query
      .order('signal_timestamp', { ascending: false })
      .range(from, to);
    
    if (error) throw error;
    
    return {
      signals: signals as ForwardedSignal[] || [],
      total: count || 0,
      page,
      pageSize
    };
  } catch (error) {
    console.error('Error fetching bot exit signals:', error);
    return {
      signals: [],
      total: 0,
      page,
      pageSize
    };
  }
}

export async function fetchType2SystemStats(timeRange: 'today' | '7days' | '30days' | 'all' = 'all'): Promise<Type2SystemStats> {
  try {
    // Get time filter
    let timeFilter: Date | null = null;
    if (timeRange === 'today') {
      timeFilter = new Date();
      timeFilter.setHours(0, 0, 0, 0);
    } else if (timeRange === '7days') {
      timeFilter = new Date();
      timeFilter.setDate(timeFilter.getDate() - 7);
    } else if (timeRange === '30days') {
      timeFilter = new Date();
      timeFilter.setDate(timeFilter.getDate() - 30);
    }

    // Get all Type 2 bots
    const { data: type2Settings } = await supabase
      .from('conditional_skip_settings')
      .select('bot_id')
      .eq('skip_type', 'FLEXIBLE_PATTERN');

    const botIds = type2Settings?.map(s => s.bot_id) || [];
    const total_bots = botIds.length;

    if (botIds.length === 0) {
      return {
        total_bots: 0,
        active_bots: 0,
        total_signals: 0,
        forwarded_signals: 0,
        skipped_signals: 0,
        total_trades: 0,
        wins: 0,
        losses: 0,
        system_win_rate: 0,
        all_signals_trades: 0,
        all_signals_wins: 0,
        all_signals_losses: 0,
        all_signals_win_rate: 0,
        win_rate_improvement_pts: 0,
        win_rate_improvement_pct: 0,
        forward_rate: 0,
        skip_rate: 0
      };
    }

    // Get current states to count active bots
    const { data: states } = await supabase
      .from('bot_current_condition_skip')
      .select('bot_id')
      .in('bot_id', botIds);
    const active_bots = states?.length || 0;

    // Build events query
    let eventsQuery = supabase
      .from('conditional_skip_events')
      .select('decision, win, action, response_status')
      .in('bot_id', botIds);

    if (timeFilter) {
      eventsQuery = eventsQuery.gte('created_at', timeFilter.toISOString());
    }

    const { data: events } = await eventsQuery;

    const total_signals = events?.length || 0;
    const forwarded_signals = events?.filter(e => e.decision === 'FORWARDED').length || 0;
    const skipped_signals = events?.filter(e => 
      e.decision === 'SKIPPED' || 
      e.decision === 'SKIPPED_BY_GROUP' ||
      e.decision === 'VIRTUAL_FORWARD'
    ).length || 0;

    // Calculate trades (only EXIT signals with response_status = 200, matching Monitor logic)
    const trades = events?.filter(e => 
      e.win !== null &&
      (e.action === 'EXIT_LONG' || e.action === 'EXIT_SHORT') &&
      e.response_status === 200
    ) || [];
    const total_trades = trades.length;
    const wins = trades.filter(e => e.win === 1).length;
    const losses = trades.filter(e => e.win === 0).length;
    const system_win_rate = total_trades > 0 ? (wins / total_trades) * 100 : 0;

    // Calculate "All Signals" win rate (before Type 2 filter)
    const allExitSignals = events?.filter(e => 
      e.win !== null &&
      (e.action === 'EXIT_LONG' || e.action === 'EXIT_SHORT')
    ) || [];

    const all_signals_trades = allExitSignals.length;
    const all_signals_wins = allExitSignals.filter(e => e.win === 1).length;
    const all_signals_losses = allExitSignals.filter(e => e.win === 0).length;
    const all_signals_win_rate = all_signals_trades > 0 
      ? (all_signals_wins / all_signals_trades) * 100 
      : 0;

    // Calculate improvement
    const win_rate_improvement_pts = system_win_rate - all_signals_win_rate;
    const win_rate_improvement_pct = all_signals_win_rate > 0
      ? ((system_win_rate - all_signals_win_rate) / all_signals_win_rate) * 100
      : 0;

    const forward_rate = total_signals > 0 ? (forwarded_signals / total_signals) * 100 : 0;
    const skip_rate = total_signals > 0 ? (skipped_signals / total_signals) * 100 : 0;

    return {
      total_bots,
      active_bots,
      total_signals,
      forwarded_signals,
      skipped_signals,
      total_trades,
      wins,
      losses,
      system_win_rate,
      all_signals_trades,
      all_signals_wins,
      all_signals_losses,
      all_signals_win_rate,
      win_rate_improvement_pts,
      win_rate_improvement_pct,
      forward_rate,
      skip_rate
    };
  } catch (error) {
    console.error('Error fetching Type 2 system stats:', error);
    return {
      total_bots: 0,
      active_bots: 0,
      total_signals: 0,
      forwarded_signals: 0,
      skipped_signals: 0,
      total_trades: 0,
      wins: 0,
      losses: 0,
      system_win_rate: 0,
      all_signals_trades: 0,
      all_signals_wins: 0,
      all_signals_losses: 0,
      all_signals_win_rate: 0,
      win_rate_improvement_pts: 0,
      win_rate_improvement_pct: 0,
      forward_rate: 0,
      skip_rate: 0
    };
  }
}

export async function fetchType2BotPerformance(timeRange: 'today' | '7days' | '30days' | 'all' = 'all'): Promise<Type2BotPerformance[]> {
  try {
    // Get time filter
    let timeFilter: Date | null = null;
    if (timeRange === 'today') {
      timeFilter = new Date();
      timeFilter.setHours(0, 0, 0, 0);
    } else if (timeRange === '7days') {
      timeFilter = new Date();
      timeFilter.setDate(timeFilter.getDate() - 7);
    } else if (timeRange === '30days') {
      timeFilter = new Date();
      timeFilter.setDate(timeFilter.getDate() - 30);
    }

    // Get all Type 2 bots with names
    const { data: type2Settings } = await supabase
      .from('conditional_skip_settings')
      .select('bot_id, bots(name)')
      .eq('skip_type', 'FLEXIBLE_PATTERN');

    if (!type2Settings || type2Settings.length === 0) {
      return [];
    }

    const performances = await Promise.all(
      type2Settings.map(async (setting) => {
        const botId = setting.bot_id;
        const botName = (setting as any).bots?.name || 'Unknown';

        // Build events query
        let eventsQuery = supabase
          .from('conditional_skip_events')
          .select('decision, win, created_at, action, response_status')
          .eq('bot_id', botId);

        if (timeFilter) {
          eventsQuery = eventsQuery.gte('created_at', timeFilter.toISOString());
        }

        const { data: events } = await eventsQuery;

        const total_signals = events?.length || 0;
        const forwarded = events?.filter(e => e.decision === 'FORWARDED').length || 0;
        const skipped = events?.filter(e => 
          e.decision === 'SKIPPED' || 
          e.decision === 'SKIPPED_BY_GROUP' ||
          e.decision === 'VIRTUAL_FORWARD'
        ).length || 0;

        // Calculate trades (only EXIT signals with response_status = 200, matching Monitor logic)
        const trades = events?.filter(e => 
          e.win !== null &&
          (e.action === 'EXIT_LONG' || e.action === 'EXIT_SHORT') &&
          e.response_status === 200
        ) || [];
        const total_trades = trades.length;
        const wins = trades.filter(e => e.win === 1).length;
        const losses = trades.filter(e => e.win === 0).length;
        const win_rate = total_trades > 0 ? (wins / total_trades) * 100 : 0;
        const forward_rate = total_signals > 0 ? (forwarded / total_signals) * 100 : 0;

        // Calculate "All Signals" for this bot
        const allBotExitSignals = events?.filter(e => 
          e.win !== null &&
          (e.action === 'EXIT_LONG' || e.action === 'EXIT_SHORT')
        ) || [];

        const all_signals_trades = allBotExitSignals.length;
        const all_signals_wins = allBotExitSignals.filter(e => e.win === 1).length;
        const all_signals_losses = allBotExitSignals.filter(e => e.win === 0).length;
        const all_signals_win_rate = all_signals_trades > 0 
          ? (all_signals_wins / all_signals_trades) * 100 
          : 0;

        // Calculate improvement
        const win_rate_improvement_pts = win_rate - all_signals_win_rate;
        const win_rate_improvement_pct = all_signals_win_rate > 0
          ? ((win_rate - all_signals_win_rate) / all_signals_win_rate) * 100
          : 0;

        // Get last signal time
        const last_signal_at = events && events.length > 0
          ? events.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0].created_at
          : null;

        // Check if bot is active and get lock pattern info
        const { data: state } = await supabase
          .from('bot_current_condition_skip')
          .select(`
            id,
            active_lock_pattern_id,
            flexible_skip_patterns!inner(is_permanent, lock_pattern)
          `)
          .eq('bot_id', botId)
          .maybeSingle();

        return {
          bot_id: botId,
          bot_name: botName,
          total_signals,
          forwarded,
          skipped,
          trades: total_trades,
          wins,
          losses,
          win_rate,
          all_signals_trades,
          all_signals_wins,
          all_signals_losses,
          all_signals_win_rate,
          win_rate_improvement_pts,
          win_rate_improvement_pct,
          forward_rate,
          is_active: !!state,
          last_signal_at,
          active_lock_pattern_id: state?.active_lock_pattern_id,
          is_permanent_locked: (state as any)?.flexible_skip_patterns?.is_permanent || false,
          lock_pattern: (state as any)?.flexible_skip_patterns?.lock_pattern
        };
      })
    );

    return performances.sort((a, b) => b.total_signals - a.total_signals);
  } catch (error) {
    console.error('Error fetching Type 2 bot performance:', error);
    return [];
  }
}
